import * as React from 'react';
import PropTypes from 'prop-types';
import { Tabs, Tab, Typography, Box, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const StyledTabs = styled(Tabs)({
  minWidth: 0,
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTabs-scroller': {
    overflow: 'hidden !important',
  },
  '& .MuiTabs-scrollButtons': {
    display: 'none',
  },
});

const StyledTab = styled(Tab)(({ theme }) => ({
  flex: '0 0 auto',
  textTransform: 'none',
  minWidth: 80,
  fontWeight: theme.typography.fontWeightRegular,
  marginInlineEnd: theme.spacing(1),
  color: 'rgba(0, 0, 0, 0.85)',
  position: 'relative',
  padding: '8px 12px',
  '&.Mui-selected': {
    backgroundColor: `${theme.palette.mode === 'dark' ? 'black' : '#E0FFE1'}`,
    borderRadius: '8px 8px 0 0',
    color: '#30A14E',
    fontWeight: 'bold',
  },
  '&.Mui-selected::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '25%',
    width: '50%',
    borderBottom: `3px solid ${theme.palette.mode === 'dark' ? 'black' : '#30A14E'}`,
  },
  '&:hover': {
    backgroundColor: '#E0FFE1',
    borderRadius: '8px 8px 0 0',
    color: `${theme.palette.mode === 'dark' ? 'black' : '#30A14E'}`,
  },
}));

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const SCROLL_AMOUNT = 200;

// Arrow button extracted outside component to avoid re-creation on each render
const ArrowBtn = ({ onClick, show, children }) => (
  <IconButton
    onClick={onClick}
    size="small"
    sx={{
      flexShrink: 0,
      width: 32,
      height: 32,
      border: '1px solid #e0e0e0',
      borderRadius: '50%',
      background: '#fff',
      boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
      opacity: show ? 1 : 0,
      pointerEvents: show ? 'auto' : 'none',
      transition: 'opacity 0.2s',
      '&:hover': { background: '#f5f5f5' },
    }}
  >
    {children}
  </IconButton>
);

ArrowBtn.propTypes = {
  onClick: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

export default function BasicTabs({ tabs }) {
  const [value, setValue] = React.useState(0);
  const { i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const scrollerRef = React.useRef(null);
  const [showStart, setShowStart] = React.useState(false);
  const [showEnd, setShowEnd] = React.useState(false);

  const checkScroll = React.useCallback(() => {
    const el = scrollerRef.current?.querySelector('.MuiTabs-scroller');
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;

    // In RTL, browsers use negative scrollLeft or positive depending on engine
    // We normalise: distance from start = |scrollLeft|
    const scrollPos = Math.abs(scrollLeft);
    const maxScroll = scrollWidth - clientWidth;

    setShowStart(scrollPos > 2);
    setShowEnd(scrollPos < maxScroll - 2);
  }, []);

  React.useEffect(() => {
    const el = scrollerRef.current?.querySelector('.MuiTabs-scroller');
    // Delay to let MUI render fully
    const timer = setTimeout(checkScroll, 100);
    if (el) {
      el.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
    }
    return () => {
      clearTimeout(timer);
      if (el) {
        el.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      }
    };
  }, [checkScroll, tabs]);

  // Scroll toward the start (right side in RTL, left side in LTR)
  const handleScrollStart = () => {
    const el = scrollerRef.current?.querySelector('.MuiTabs-scroller');
    if (!el) return;
    el.scrollBy({ left: isRtl ? SCROLL_AMOUNT : -SCROLL_AMOUNT, behavior: 'smooth' });
  };

  // Scroll toward the end (left side in RTL, right side in LTR)
  const handleScrollEnd = () => {
    const el = scrollerRef.current?.querySelector('.MuiTabs-scroller');
    if (!el) return;
    el.scrollBy({ left: isRtl ? -SCROLL_AMOUNT : SCROLL_AMOUNT, behavior: 'smooth' });
  };

  const handleChange = (_, newValue) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          marginBottom: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {/* Start arrow: ← in LTR, → in RTL */}
        <ArrowBtn onClick={handleScrollStart} show={showStart}>
          {isRtl ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
        </ArrowBtn>

        <Box ref={scrollerRef} sx={{ flex: 1, overflow: 'hidden' }}>
          <StyledTabs
            dir={isRtl ? 'rtl' : 'ltr'}
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons={false}
            sx={{ direction: isRtl ? 'rtl' : 'ltr' }}
          >
            {tabs.map((tab, index) => (
              <StyledTab key={index} label={tab.label} {...a11yProps(index)} />
            ))}
          </StyledTabs>
        </Box>

        {/* End arrow: → in LTR, ← in RTL */}
        <ArrowBtn onClick={handleScrollEnd} show={showEnd}>
          {isRtl ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </ArrowBtn>
      </Box>

      {tabs.map((tab, index) => (
        <CustomTabPanel key={index} value={value} index={index}>
          {tab.content}
        </CustomTabPanel>
      ))}
    </Box>
  );
}

BasicTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      content: PropTypes.node.isRequired,
    })
  ).isRequired,
};
